import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useStats } from "./StatsContext";
import styles from "./RecipientSelector.module.css";

const DEBOUNCE_MS = 300;

const ENGAGEMENT_COLORS = {
  High: "#1a7f4b",
  Medium: "#b87d00",
  Low: "#9e2a2a",
};

const RecipientSelector = () => {
  const {
    recipientList,
    recipientListMeta,
    recipientSearchQuery,
    setRecipientSearchQuery,
    loadRecipientList,
    selectedRecipientId,
    setSelectedRecipientId,
  } = useStats();

  const { hash, pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState(recipientSearchQuery);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const { tl, doc } = window?.objInit || {};

  // Determine context: KDF or LaserLink
  const isKdf = pathname.includes("kdf-stats");
  const type = isKdf ? "doc" : "tl";
  const token = isKdf ? doc?.group_id : tl?.token;
  const contextName = isKdf ? "KDF" : "LaserLINK";

  // Load recipients for the current context on mount
  useEffect(() => {
    if (token) {
      loadRecipientList(1, "", token, type);
    }
  }, [loadRecipientList, token, type]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  // Debounced search
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLocalSearch(value);

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setRecipientSearchQuery(value);
        loadRecipientList(1, value, token, type);
      }, DEBOUNCE_MS);
    },
    [setRecipientSearchQuery, loadRecipientList, token, type]
  );

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 50;

    if (nearBottom && recipientListMeta.hasNextPage && !recipientListMeta.isLoading) {
      loadRecipientList(recipientListMeta.currentPage + 1, localSearch, token, type);
    }
  }, [recipientListMeta, loadRecipientList, localSearch, token, type]);

  // Select a recipient — set filter (stay on current tab)
  const handleSelect = useCallback((recipient) => {
    setIsOpen(false);
    setSelectedRecipientId(recipient.id);
  }, [setSelectedRecipientId]);

  // Clear recipient filter (stay on current tab)
  const handleClearSelection = useCallback(() => {
    setIsOpen(false);
    setSelectedRecipientId(null);
  }, [setSelectedRecipientId]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen]);

  const currentRecipient = recipientList.find((r) => r.id === selectedRecipientId);
  const displayName =
    currentRecipient?.name ||
    currentRecipient?.email ||
    "Select a Recipient";

  // If no recipients and done loading, show message instead of dropdown
  if (!recipientListMeta.isLoading && recipientList.length === 0) {
    return (
      <div className={styles.emptyMessage}>
        Sorry, no unique recipients were found for selected {contextName}
      </div>
    );
  }

  return (
    <div className={styles.selectorWrapper}>
      {/* Trigger button */}
      <div
        className={`${styles.selectorTrigger} ${isOpen ? styles.selectorTriggerOpen : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select a Recipient"
      >
        <div className={styles.triggerContent}>
          <span className={styles.recipientIcon}>&#128100;</span>
          <span className={styles.triggerTitle}>{displayName}</span>
          {currentRecipient?.percent_viewed != null && (
            <span className={styles.triggerMeta}>
              {currentRecipient.percent_viewed}% viewed
            </span>
          )}
        </div>
        <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}>
          &#9660;
        </span>
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <>
          <div className={styles.overlay} onClick={() => setIsOpen(false)} />
          <div className={styles.dropdownPanel} role="listbox">
            {/* Search */}
            <div className={styles.searchWrapper}>
              <input
                ref={searchRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search recipients..."
                value={localSearch}
                onChange={handleSearchChange}
                aria-label="Search recipients"
              />
            </div>

            {/* List */}
            <div
              ref={listRef}
              className={styles.listContainer}
              onScroll={handleScroll}
            >
              {/* Initial loading */}
              {recipientListMeta.isLoading && recipientList.length === 0 && (
                <div className={styles.loadingRow}>
                  <div className={styles.spinner}></div>
                  <span>Loading recipients...</span>
                </div>
              )}

              {/* Empty state */}
              {!recipientListMeta.isLoading && recipientList.length === 0 && (
                <div className={styles.emptyState}>
                  {localSearch
                    ? "No matching recipients found"
                    : `Sorry, no unique recipients were found for selected ${contextName}`}
                </div>
              )}

              {/* Clear selection option */}
              {selectedRecipientId && (
                <div
                  className={styles.recipientItem}
                  onClick={handleClearSelection}
                  role="option"
                  aria-selected={false}
                >
                  <div className={styles.recipientInfo}>
                    <div className={styles.recipientName} style={{ fontStyle: "italic" }}>
                      All Recipients
                    </div>
                  </div>
                </div>
              )}

              {/* Recipient items */}
              {recipientList.map((recipient) => (
                <div
                  key={recipient.id}
                  className={`${styles.recipientItem} ${
                    recipient.id === selectedRecipientId
                      ? styles.recipientItemActive
                      : ""
                  }`}
                  onClick={() => handleSelect(recipient)}
                  role="option"
                  aria-selected={recipient.id === selectedRecipientId}
                >
                  <div className={styles.recipientAvatar}>
                    {(recipient.name || recipient.email || "?")[0].toUpperCase()}
                  </div>
                  <div className={styles.recipientInfo}>
                    <div className={styles.recipientName}>
                      {recipient.name || recipient.email || "Anonymous"}
                    </div>
                    {recipient.email && recipient.name && (
                      <div className={styles.recipientEmail}>{recipient.email}</div>
                    )}
                  </div>
                  <div className={styles.recipientStats}>
                      {recipient.percent_viewed &&
                          <div className={styles.recipientPercent}>
                              {recipient.percent_viewed}%
                          </div>
                      }
                    {recipient.engagement && (
                      <div
                        className={styles.recipientEngagement}
                        style={{
                          color: ENGAGEMENT_COLORS[recipient.engagement] || "#888",
                        }}
                      >
                        {recipient.engagement}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Loading more (infinite scroll) */}
              {recipientListMeta.isLoading && recipientList.length > 0 && (
                <div className={styles.loadingRow}>
                  <div className={styles.spinner}></div>
                  <span>Loading more...</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RecipientSelector;
